/**
 * DesignSettings — Settings → Design (SD-8.1, FR-33, FR-39, EXPERIENCE.md §C).
 *
 * Follows the Settings card idiom (shadcn Card, sonner toast, the service seam; it began as a copy of
 * the Contracts access card RBAC-6.2 removed). It reads and writes `solar_design_settings`, NEVER `tenant_settings` —
 * the 20260803 migration moved these columns off tenant_settings and this screen must not
 * quietly point back.
 *
 * GATED (FR-29): rendered only when `useDesignFeatureAccess` allows, and NOTHING renders while
 * the gate resolves — the SD-2.1 rule that a section which appears and then vanishes has already
 * told the user a pre-release, metered feature exists. Superadmins always pass, which is what
 * lets them configure a tenant before opening the gate for it.
 *
 * HOUSE-STYLE FORM THROUGHOUT (§C.2), with the one specified exception: the setbacks card
 * carries SD-8.2's slider-plus-inline-edit control and the live envelope preview INSIDE the
 * card. Setbacks are the only geometric setting here — nobody needs a picture of $0.171/kWh —
 * so the financial fields stay plain inputs and the inline-edit affordance is sliders-only.
 *
 * TWO COMMITS, NOT ONE (UX-DR16): the slider drag and the inline checkmark write the FORM via
 * `setFieldValue`; the save bar owns the workspace. Both paths land in the same `values` state,
 * so the one `changedFields` diff drives "Unsaved changes" for every field — there is no second
 * dirty flag to fall out of sync.
 *
 * THE EQUIPMENT CARD (SD-8.4) picks the tenant default from the SD-4.4 library and opens the
 * Manage modules dialog — a dialog, deliberately not a settings page. The default writes
 * `default_module_id` through the SAME form state and save path as every other field, so the
 * dirty diff, the save bar and savedConfirmation all cover it for free. Changing it touches no
 * existing design (resolveDefaultPanelModule reads it at design-open, and the copy says so). A
 * default that has since been DEACTIVATED still renders as the selected option — marked, not
 * blanked — because a select that shows empty for a real stored value reads as data loss; it is
 * simply no longer offered once deselected.
 *
 * SAVE CONFIRMS WHAT CHANGED (§C.5 "Saved"): the pinned `saved_setback` string when the ridge
 * changed, named fields otherwise, always ending on existing-designs-untouched — the sentence
 * that prevents the support ticket. A save that changes nothing writes nothing.
 */
import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DollarSign, Info, Loader2, Ruler, Save, Sun } from 'lucide-react';
import { toast } from 'sonner';

import { usePermissions } from '@/contexts/PermissionContext';
import { useDesignFeatureAccess } from '@/hooks/useDesignFeatureAccess';
import { EMPTY_AND_ERROR } from '@/lib/solarDesignCopy';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type PvModuleRow,
  getModuleById,
  listActiveModules,
} from '@/services/pvModulesService';
import {
  getSolarDesignSettings,
  updateSolarDesignSettings,
} from '@/services/solarDesignSettingsService';
import {
  type DesignSettingsField,
  type DesignSettingsValues,
  type EditorStamp,
  changedFields,
  fieldBadge,
  savedConfirmation,
} from './designSettingsForm';
import { saveErrorMessage } from './designSettingsStates';
import { FeatureAccessNotice } from '@/components/settings/FeatureAccessNotice';
import { DESIGN_USE_PERMISSION } from '@/lib/designAccess';
import { PERMISSION_DISPLAY } from '@/lib/permissionDisplay';
import { EnvelopePreview } from './EnvelopePreview';
import { ManageModulesDialog } from './ManageModulesDialog';
import { formatEfficiency, physicalSize } from './moduleFacts';
import { setbacksFromValues } from './referenceRoof';
import { SetbackSliderField } from './SetbackSliderField';

/** SelectItem values cannot be empty, so "no tenant default" rides a sentinel, never ''. */
const SHIPPED_MODULE_SENTINEL = 'shipped-default';

/** One labelled numeric field with its UX-DR18 badge. The unit is RENDERED, never typed. */
function NumberField({
  id,
  label,
  unit,
  field,
  values,
  stamp,
  step,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  unit: string;
  field: DesignSettingsField;
  values: DesignSettingsValues;
  stamp: EditorStamp;
  step: string;
  disabled: boolean;
  onChange: (field: DesignSettingsField, raw: string) => void;
}) {
  const badge = fieldBadge(field, values, stamp);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        <Badge
          variant="outline"
          className={
            badge.kind === 'yours' ? 'text-foreground' : 'text-muted-foreground border-dashed'
          }
        >
          {badge.label}
        </Badge>
      </div>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          step={step}
          value={String(values[field] ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(field, e.target.value)}
          className="max-w-[10rem]"
        />
        <span className="text-sm text-muted-foreground">{unit}</span>
      </div>
    </div>
  );
}

export function DesignSettings() {
  const { hasAccess, loading: accessLoading } = useDesignFeatureAccess();
  // SD-8.3 (UX-DR17): a role without manage_settings sees everything READ-ONLY with the reason
  // in words — the database trigger and RLS are defence in depth, never the user experience.
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('manage_settings');
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [saved, setSaved] = useState<DesignSettingsValues | null>(null);
  const [values, setValues] = useState<DesignSettingsValues | null>(null);
  const [stamp, setStamp] = useState<EditorStamp>({ editorName: null, updatedAt: null });
  const [saving, setSaving] = useState(false);
  // SD-8.4: the module catalogue behind the default select, and the manage dialog.
  const [moduleOptions, setModuleOptions] = useState<PvModuleRow[] | null>(null);
  const [inactiveDefault, setInactiveDefault] = useState<PvModuleRow | null>(null);
  const [modulesDialogOpen, setModulesDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const result = await getSolarDesignSettings();
      if (cancelled) return;
      if (!result) {
        toast.error('Failed to load design settings');
      } else if (result.missing) {
        // UX-DR17: detected ON LOAD, not on save. SD-8.3 renders the full state matrix; this
        // story surfaces the string and refuses to offer a save that cannot succeed.
        setMissing(true);
      } else {
        setSaved(result.row);
        setValues(result.row);
        setStamp({ editorName: result.editorName, updatedAt: result.row.updated_at });
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const onChange = (field: DesignSettingsField, raw: string) => {
    setValues((current) => {
      if (!current) return current;
      const n = Number(raw);
      return { ...current, [field]: raw === '' || !Number.isFinite(n) ? current[field] : n };
    });
  };

  // SD-8.2: the sliders and the inline editor commit NUMBERS, already validated by
  // resolveInlineEdit — same `values` state as onChange, so `changedFields` flips the save bar
  // to "Unsaved changes" the moment an inline edit lands (UX-DR16).
  const setFieldValue = (field: DesignSettingsField, value: number) => {
    setValues((current) => (current ? { ...current, [field]: value } : current));
  };

  // SD-8.4: the default module rides the SAME form state, so the dirty diff and the save path
  // cover it exactly like every numeric field.
  const setModuleDefault = (id: string | null) => {
    setValues((current) => (current ? { ...current, default_module_id: id } : current));
  };

  useEffect(() => {
    let cancelled = false;
    listActiveModules().then((rows) => {
      if (!cancelled) setModuleOptions(rows);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // A stored default that is no longer in the active catalogue (deactivated since it was
  // chosen) still has to render as the selection — a blank select over a real value reads as
  // data loss. Resolve it by id; FR-20 keeps inactive rows resolvable.
  useEffect(() => {
    const id = values?.default_module_id;
    if (!id || moduleOptions === null || moduleOptions.some((m) => m.id === id)) {
      setInactiveDefault(null);
      return;
    }
    let cancelled = false;
    getModuleById(id).then((row) => {
      if (!cancelled) setInactiveDefault(row);
    });
    return () => {
      cancelled = true;
    };
  }, [values?.default_module_id, moduleOptions]);

  const dirty = saved && values ? changedFields(saved, values) : [];

  const handleSave = async () => {
    if (!saved || !values) return;
    const changed = changedFields(saved, values);
    // A save that changes nothing writes no row and says so quietly.
    if (changed.length === 0) {
      toast.info('Nothing changed.');
      return;
    }
    setSaving(true);
    try {
      const patch = Object.fromEntries(changed.map((f) => [f, values[f]]));
      const row = await updateSolarDesignSettings(patch);
      setSaved(row);
      setValues(row);
      setStamp((s) => ({ editorName: s.editorName, updatedAt: row.updated_at }));
      toast.success(savedConfirmation(changed, row));
    } catch (error) {
      // SD-8.3: "could not reach the server, retry" and "you do not have permission" are
      // different problems with different next actions. A 42501 carries the server's own words
      // (columns + who to ask) and is surfaced verbatim; anything else says to retry.
      console.error('[DesignSettings] Failed to save design settings:', error);
      toast.error(saveErrorMessage(error).message);
    } finally {
      setSaving(false);
    }
  };

  // Absent from the DOM while resolving and when denied — the SD-2.1 gate rule.
  if (accessLoading || !hasAccess) return null;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (missing || !values || !saved) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sun className="h-5 w-5" /> Design Defaults
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>{EMPTY_AND_ERROR.save_uninitialised}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const stampProps = { values, stamp };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ruler className="h-5 w-5" /> Setbacks
          </CardTitle>
          <CardDescription>
            How far panels stay from each edge type on new designs. Existing designs keep the
            values they were drawn with.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-3">
            <SetbackSliderField id="setback-ridge" label="Ridge" unit="in" field="setback_ridge_in" disabled={saving || !canManage} onCommit={setFieldValue} {...stampProps} />
            <SetbackSliderField id="setback-eave" label="Eave" unit="in" field="setback_eave_in" disabled={saving || !canManage} onCommit={setFieldValue} {...stampProps} />
            <SetbackSliderField id="setback-rake" label="Rake" unit="in" field="setback_rake_in" disabled={saving || !canManage} onCommit={setFieldValue} {...stampProps} />
          </div>
          {/* UX-DR14: the preview lives INSIDE this card — it is about these three numbers and
              nothing else on the page. It reads the LIVE form values, so it moves with every
              drag and every inline commit, saved or not. */}
          <EnvelopePreview setbacks={setbacksFromValues(values)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" /> Financial Defaults
          </CardTitle>
          <CardDescription>
            The numbers every new design starts its savings estimate from.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <NumberField id="utility-rate" label="Utility rate" unit="$ / kWh" field="utility_rate_per_kwh" step="0.0001" disabled={saving || !canManage} onChange={onChange} {...stampProps} />
          <NumberField id="utility-escalation" label="Utility escalation" unit="% / year" field="utility_escalation_pct" step="0.1" disabled={saving || !canManage} onChange={onChange} {...stampProps} />
          <NumberField id="price-per-watt" label="Price per watt" unit="$ / W DC" field="price_per_watt" step="0.01" disabled={saving || !canManage} onChange={onChange} {...stampProps} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sun className="h-5 w-5" /> Equipment
          </CardTitle>
          <CardDescription>The module new designs are sized and priced with.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="default-module">Default module</Label>
            <Badge
              variant="outline"
              className={
                fieldBadge('default_module_id', values, stamp).kind === 'yours'
                  ? 'text-foreground'
                  : 'text-muted-foreground border-dashed'
              }
            >
              {fieldBadge('default_module_id', values, stamp).label}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={values.default_module_id ?? SHIPPED_MODULE_SENTINEL}
              onValueChange={(v) => setModuleDefault(v === SHIPPED_MODULE_SENTINEL ? null : v)}
              disabled={saving || !canManage || moduleOptions === null}
            >
              <SelectTrigger id="default-module" className="w-full max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SHIPPED_MODULE_SENTINEL}>
                  Shipped default module (400 W generic)
                </SelectItem>
                {(moduleOptions ?? []).map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.manufacturer} {m.model} — {m.watts} W
                  </SelectItem>
                ))}
                {inactiveDefault && (
                  <SelectItem value={inactiveDefault.id}>
                    {inactiveDefault.manufacturer} {inactiveDefault.model} —{' '}
                    {inactiveDefault.watts} W (deactivated)
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {/* Opening the dialog is read-safe for everyone; the dialog gates its own writes. */}
            <Button variant="outline" size="sm" onClick={() => setModulesDialogOpen(true)}>
              Manage modules
            </Button>
          </div>
          {(() => {
            const selected =
              (moduleOptions ?? []).find((m) => m.id === values.default_module_id) ??
              (inactiveDefault?.id === values.default_module_id ? inactiveDefault : null);
            return selected ? (
              <p className="text-xs text-muted-foreground">
                {physicalSize(selected.width_mm, selected.height_mm)} ·{' '}
                {formatEfficiency(selected.watts, selected.width_mm, selected.height_mm)} efficient
              </p>
            ) : null;
          })()}
          <p className="text-xs text-muted-foreground">
            New designs start with this module. Designs that already exist keep the module they
            were drawn with.
          </p>
        </CardContent>
      </Card>

      {/* SD-8.3: the action bar. Without manage_settings there is NO button — a button that
          will fail is not a control — and the copy says who to ask instead. */}
      {canManage ? (
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving || dirty.length === 0}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Design Defaults
          </Button>
          {dirty.length > 0 && (
            <span className="text-sm text-muted-foreground">Unsaved changes</span>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{EMPTY_AND_ERROR.save_no_permission}</p>
      )}

      {/* RBAC-6.2: who uses Design is the design.use permission, chosen in Roles & Permissions. The
          SD-8.3 role picker and its tenant_settings column are gone. */}
      <FeatureAccessNotice feature="Solar Design" permissionLabel={PERMISSION_DISPLAY[DESIGN_USE_PERMISSION].label} />

      {/* SD-8.4: catalogue management. A dialog off the Equipment card, never a settings page.
          When it changes the catalogue, the select's options reload. */}
      <ManageModulesDialog
        open={modulesDialogOpen}
        onOpenChange={setModulesDialogOpen}
        canManage={canManage}
        onCatalogueChanged={() => {
          void listActiveModules().then(setModuleOptions);
        }}
      />
    </div>
  );
}
